import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "../utils/axiosInstance";
import Layout from "./Layout";
import LoadingError from "./LoadingError";
import { useAuth } from "../utils/AuthContext";
import ReportsFilters from "./ReportsFilters";
import ReportsTableView from "./ReportsTableView";
import AddEntryModal from "./AddEntryModal";
import DetailModal from "./DetailModal";
import {
  getVisibleColumnsForUser,
  processCreatedAt,
} from "../utils/columnConfig";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const ReportsTable = () => {
  const { user, hasCheckedAuth } = useAuth();
  const userManufacturer = user?.manufacturer;

  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and display state
  const [filters, setFilters] = useState({});
  const [latestOnly, setLatestOnly] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnsInitialized, setColumnsInitialized] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Modal state
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, details: [] });

  // Get columns for grouping (used in data fetching logic)
  const getGroupByColumns = useCallback(() => {
    const groupableColumns = [
      "filename",
      "payer",
      "stage",
      "created_at",
      "job_id",
      "year",
      "period",
    ];
    if (userManufacturer === "ciita") {
      groupableColumns.push("manufacturer");
    }

    return groupableColumns.filter((col) => visibleColumns.has(col));
  }, [visibleColumns, userManufacturer]);

  // Determine if we're in grouping mode
  const isGrouping = useMemo(() => {
    const allGroupableColumns = [
      "filename",
      "payer",
      "stage",
      "created_at",
      "job_id",
      "year",
      "period",
    ];
    if (userManufacturer === "ciita") {
      allGroupableColumns.push("manufacturer");
    }

    const visibleGroupableCount = allGroupableColumns.filter((col) =>
      visibleColumns.has(col),
    ).length;

    return (
      visibleColumns.size > 0 &&
      visibleGroupableCount < allGroupableColumns.length
    );
  }, [visibleColumns, userManufacturer]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!userManufacturer || !columnsInitialized) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = isGrouping
        ? `${API_URL}/reports/aggregated_view`
        : `${API_URL}/reports/detail_list`;

      const params = {};

      if (latestOnly) {
        params.latest_only = "true";
      }

      // Send visible columns to backend
      params.columns = Array.from(visibleColumns).join(",");

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && (userManufacturer === "ciita" || key !== "manufacturer")) {
          params[`filter_${key}`] = value;
        }
      });

      const response = await axios.get(endpoint, {
        params,
        withCredentials: true,
      });

      const processedData = response.data.map((row) => {
        if (row.created_at) {
          return {
            ...row,
            created_at: processCreatedAt(row.created_at),
          };
        }
        return row;
      });

      setData(processedData);
      setPage(0); // Reset to first page on new data
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    userManufacturer,
    filters,
    latestOnly,
    isGrouping,
    visibleColumns,
    columnsInitialized,
  ]);

  // Initialize columns when user auth completes, then fetch data
  useEffect(() => {
    if (!hasCheckedAuth || !userManufacturer) {
      return;
    }

    // Initialize columns on first load
    if (!columnsInitialized) {
      const { defaultColumns, allColumns } =
        getVisibleColumnsForUser(userManufacturer);

      setVisibleColumns(defaultColumns);
      setColumnOrder(allColumns);
      setColumnsInitialized(true);

      // Clear manufacturer filter if not ciita
      if (userManufacturer !== "ciita") {
        setFilters((prev) => {
          const { manufacturer, ...rest } = prev;
          return rest;
        });
      }
      return; // Don't fetch yet, wait for next render when columns are set
    }

    // Only fetch after columns are initialized
    fetchData();
  }, [
    hasCheckedAuth,
    userManufacturer,
    columnsInitialized,
    filters,
    latestOnly,
    isGrouping,
    fetchData,
  ]);

  // Sort data in memory (client-side sorting)
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return data;

    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bVal == null) return sortConfig.direction === "asc" ? -1 : 1;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  // Handlers
  const handleSort = useCallback((column) => {
    setSortConfig((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const handleFilterChange = useCallback((column, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [column]: value };

      // Clear period when year changes
      if (column === "year" && prev.year && prev.year !== value) {
        newFilters.period = "";
      }

      return newFilters;
    });
  }, []);

  const handleReset = useCallback(() => {
    setFilters({});
    setSortConfig({ column: null, direction: "asc" });
    setPage(0);
  }, []);

  const handleToggleColumn = useCallback(
    (column) => {
      const requiredColumns = ["stage"];
      if (userManufacturer === "ciita") {
        requiredColumns.push("manufacturer");
      }

      if (requiredColumns.includes(column)) return;

      setVisibleColumns((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(column)) {
          newSet.delete(column);
        } else {
          newSet.add(column);
        }
        return newSet;
      });
    },
    [userManufacturer],
  );

  const handleMoveColumn = useCallback((column, direction) => {
    setColumnOrder((prev) => {
      const idx = prev.indexOf(column);
      if (idx === -1) return prev;

      const newOrder = [...prev];
      if (direction === "up" && idx > 0) {
        [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
      } else if (direction === "down" && idx < newOrder.length - 1) {
        [newOrder[idx + 1], newOrder[idx]] = [newOrder[idx], newOrder[idx + 1]];
      }
      return newOrder;
    });
  }, []);

  const handleOpenDetailModal = useCallback(
    async (row) => {
      const isSummaryRow = row.is_summary === true;

      // If it's a detail row, just show it
      if (!isSummaryRow && row.id) {
        setDetailModal({ open: true, details: [row] });
        return;
      }

      // If it's a summary row with no data, do nothing
      if (!row.num_claims && !row.dollar_amount) {
        return;
      }

      // Fetch drill-down details for summary row
      try {
        const params = {};

        if (latestOnly) {
          params.latest_only = "true";
        }

        // Send visible columns for drill-down
        const { allColumns } = getVisibleColumnsForUser(userManufacturer);
        params.columns = allColumns.join(",");

        // Apply current filters
        Object.entries(filters).forEach(([key, value]) => {
          if (
            value &&
            (userManufacturer === "ciita" || key !== "manufacturer")
          ) {
            params[`filter_${key}`] = value;
          }
        });

        // Apply row-specific filters for drill-down
        const groupingColumns = getGroupByColumns();
        groupingColumns.forEach((col) => {
          let rowValue = row[col];
          if (col === "created_at") {
            params[`filter_${col}`] = rowValue.raw ?? rowValue;
          } else {
            params[`filter_${col}`] = rowValue;
          }
        });

        const response = await axios.get(`${API_URL}/reports/detail_list`, {
          params,
          withCredentials: true,
        });

        setDetailModal({ open: true, details: response.data });
      } catch (err) {
        console.error("Error fetching summary details:", err);
        alert("Failed to load summary details");
      }
    },
    [userManufacturer, latestOnly, filters, getGroupByColumns],
  );

  const handleCloseDetailModal = useCallback(() => {
    setDetailModal({ open: false, details: [] });
  }, []);

  // Render guards
  if (!hasCheckedAuth) {
    return (
      <Layout>
        <LoadingError isLoading={true} error={null} />
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Typography variant="h5" color="error">
          Access Denied: User not authenticated.
        </Typography>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          width: "100%",
          overflowX: "hidden",
          minHeight: "600px",
        }}
      >
        {/* Filters and Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <ReportsFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            latestOnly={latestOnly}
            onLatestOnlyChange={setLatestOnly}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
            columnOrder={columnOrder}
            onMoveColumn={handleMoveColumn}
            data={data}
            userManufacturer={userManufacturer}
            hasActiveFilters={
              Object.keys(filters).length > 0 || sortConfig.column
            }
          />

          {userManufacturer === "ciita" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setAddEntryModalOpen(true)}
            >
              Add Entry
            </Button>
          )}
        </Box>

        {/* Table */}
        <ReportsTableView
          data={sortedData}
          loading={loading}
          error={error}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
          sortConfig={sortConfig}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onRowClick={handleOpenDetailModal}
          userManufacturer={userManufacturer}
        />
      </Box>

      {/* Modals */}
      <AddEntryModal
        open={addEntryModalOpen}
        onClose={() => setAddEntryModalOpen(false)}
        onSuccess={fetchData}
        manufacturer={userManufacturer}
      />

      <DetailModal
        open={detailModal.open}
        details={detailModal.details}
        onClose={handleCloseDetailModal}
        userManufacturer={userManufacturer}
      />
    </Layout>
  );
};

export default ReportsTable;
